import type { AxiosInstance, AxiosResponse } from 'axios';
import type { Optional } from '@gov-nx/core/types';
import type {
	ePetitionPetitionersDownloadQueryProps,
	PeticeClenPeticnihoVyboruSeznamDto,
	PeticeOvmSeznamDto,
	PeticePeticeDto,
	PeticePeticeSeznamDto,
	PeticePrilohaSeznamDto,
} from '..';

export interface EPetitionListFilter {
	text: Optional<string>;
	mnouPodepsane: boolean;
	mnouZalozene: boolean;
	start: number;
	pocet: number;
	platnostOd: Optional<string> | null;
	platnostDo: Optional<string> | null;
}

export const ePetitionQuery = async (
	axiosInstance: AxiosInstance,
	filter: EPetitionListFilter
): Promise<PeticePeticeSeznamDto> => {
	const { data } = await axiosInstance.get('/api/kompozitni/v1/petice', {
		params: filter,
	});
	return data;
};
export const ePetitionDetailQuery = async (
	axiosInstance: AxiosInstance,
	petitionId: number
): Promise<PeticePeticeDto> => {
	const { data } = await axiosInstance.get(
		'/api/kompozitni/v1/petice/' + petitionId
	);
	return data;
};
export const ePetitionMembersQuery = async (
	axiosInstance: AxiosInstance,
	petitionId: number
): Promise<PeticeClenPeticnihoVyboruSeznamDto> => {
	const { data } = await axiosInstance.get(
		'/api/kompozitni/v1/petice/' + petitionId + '/clenove-peticniho-vyboru'
	);
	return data;
};
export const ePetitionAttachmentsQuery = async (
	axiosInstance: AxiosInstance,
	petitionId: number
): Promise<PeticePrilohaSeznamDto> => {
	const { data } = await axiosInstance.get(
		'/api/kompozitni/v1/petice/' + petitionId + '/prilohy'
	);
	return data;
};

export const ePetitionAttachmentsUpdateQuery = async ({
	axiosInstance,
	petitionId,
	files,
}: {
	axiosInstance: AxiosInstance;
	petitionId: number;
	files: [string];
}): Promise<number> => {
	const { data } = await axiosInstance.post(
		'/api/kompozitni/v1/petice/' + petitionId + '/prilohy',
		{
			soubory: files,
		},
		{
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}
	);
	return data;
};
export const ePetitionSignQuery = async ({
	axiosInstance,
	petitionId,
}: {
	axiosInstance: AxiosInstance;
	petitionId: number;
}): Promise<AxiosResponse> =>
	axiosInstance.put('/api/kompozitni/v1/petice/' + petitionId + '/podepsat');
export const ePetitionRevokeQuery = async ({
	axiosInstance,
	petitionId,
}: {
	axiosInstance: AxiosInstance;
	petitionId: number;
}): Promise<AxiosResponse> =>
	axiosInstance.put(
		'/api/kompozitni/v1/petice/' + petitionId + '/odvolat-podpis'
	);
export const ePetitionOvmDataBoxQuery = async ({
	axiosInstance,
	...params
}: {
	text: string;
	pocet: number;
	axiosInstance: AxiosInstance;
}): Promise<PeticeOvmSeznamDto> => {
	const { data } = await axiosInstance.get('/api/kompozitni/v1/ovm', {
		params,
	});
	return data;
};

export const ePetitionSetupQuery = async ({
	axiosInstance,
	...body
}: {
	zakladatelTyp?: 'FO' | 'PO';
	nazev: string;
	axiosInstance: AxiosInstance;
}): Promise<number> => {
	const { data } = await axiosInstance.post('/api/kompozitni/v1/petice', body);
	return data;
};

export const ePetitionUpdateQuery = async <Body,>({
	petitionId,
	body,
	axiosInstance,
}: {
	petitionId: number;
	body: Body;
	axiosInstance: AxiosInstance;
}): Promise<number> => {
	const { data } = await axiosInstance.put(
		`/api/kompozitni/v1/petice/${petitionId}`,
		body
	);
	return data;
};

export const ePetitionDeleteQuery = async (
	axiosInstance: AxiosInstance,
	petitionId: number
): Promise<number> => {
	const { data } = await axiosInstance.delete(
		`/api/kompozitni/v1/petice/${petitionId}`
	);
	return data;
};

export const ePetitionDeleteAttachmentQuery = async ({
	attachmentId,
	petitionId,
	axiosInstance,
}: {
	petitionId: number;
	attachmentId: number;
	axiosInstance: AxiosInstance;
}): Promise<number> => {
	const { data } = await axiosInstance.delete(
		`/api/kompozitni/v1/petice/${petitionId}/prilohy/${attachmentId}`
	);
	return data;
};

export const ePetitionCreateQuery = async (
	axiosInstance: AxiosInstance,
	petitionId: number
): Promise<AxiosResponse> =>
	axiosInstance.put(`/api/kompozitni/v1/petice/${petitionId}/zalozit`);

export const ePetitionExtendQuery = async (
	axiosInstance: AxiosInstance,
	petitionId: number
): Promise<number> => {
	const { data } = await axiosInstance.put(
		`/api/kompozitni/v1/petice/${petitionId}/prodlouzit`
	);
	return data;
};

export const ePetitionCloseQuery = async ({
	petitionId,
	axiosInstance,
}: {
	axiosInstance: AxiosInstance;
	petitionId: number;
}): Promise<number> => {
	const { data } = await axiosInstance.put(
		`/api/kompozitni/v1/petice/${petitionId}/uzavrit`
	);
	return data;
};

export const ePetitionSubmitQuery = async ({
	petitionId,
	axiosInstance,
}: {
	axiosInstance: AxiosInstance;
	petitionId: number;
}): Promise<AxiosResponse> =>
	axiosInstance.put(`/api/kompozitni/v1/petice/${petitionId}/podat`);

export const ePetitionCloseAndSubmitQuery = async ({
	petitionId,
	axiosInstance,
}: {
	axiosInstance: AxiosInstance;
	petitionId: number;
}): Promise<AxiosResponse> =>
	axiosInstance.put(`/api/kompozitni/v1/petice/${petitionId}/uzavrit-a-podat`);

export const ePetitionDownloadQuery = async ({
	petitionId,
	axiosInstance,
}: {
	axiosInstance: AxiosInstance;
	petitionId: number;
}) => {
	const data = await axiosInstance.get(
		`/api/kompozitni/v1/petice/${petitionId}/stahnout-vypis`,
		{
			responseType: 'blob',
		}
	);
	return data;
};

export const ePetitionAttachmentDownloadQuery = async ({
	petitionId,
	attachmentId,
	axiosInstance,
}: {
	petitionId: number;
	attachmentId: number;
	axiosInstance: AxiosInstance;
}): Promise<AxiosResponse<Blob>> => {
	const data = await axiosInstance.get(
		`api/kompozitni/v1/petice/${petitionId}/prilohy/${attachmentId}/stahnout`,
		{
			responseType: 'blob',
		}
	);
	return data;
};

export const ePetitionPetitionersDownloadQuery = async ({
	petitionId,
	format,
	axiosInstance,
}: ePetitionPetitionersDownloadQueryProps & {
	axiosInstance: AxiosInstance;
}): Promise<AxiosResponse<Blob>> => {
	const data = await axiosInstance.get(
		`/api/kompozitni/v1/petice/${petitionId}/stahnout-seznam-petentu`,
		{
			params: {
				format,
			},
			responseType: 'blob',
		}
	);
	return data;
};
